import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { useLibraryScope } from "@/lib/library/hooks/useLibraryScope";
import { ACTIVITIES, ActivityType, getActivityModal, getActivityTypeIcon } from "@/lib/builder/models/activity-configs.model";
import { ModalProps } from "@/lib/builder/models/activities-modal.model";
import { ItineraryActivity } from "@/lib/builder/models/activity.model";
import { ActivityTypePicker } from "@/lib/builder/components/ActivityTypePicker";
import { SaveToLibraryModal } from "@/lib/builder/modals/SaveToLibraryModal";
import { LibraryActivitiesTable } from "@/lib/library/LibraryActivitiesTable";
import { LibraryArchiveSelector, LibraryArchiveScope } from "@/lib/library/LibraryArchiveSelector";
import { useActivityLibraryQuery } from "@/lib/library/hooks/useActivityLibrary";
import { LIBRARY_ACTIVITY_STORAGE_ID } from "@/lib/library/models/library-activity.model";
import { LibraryFilters, LibraryOwnerScope } from "@/lib/library/components/LibraryFilters";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";

/**
 * Pagina "Libreria": una singola vista con un filtro a icone per tipo attività
 * (accommodation, activity, flight, transportation, food-drinks, events, notes),
 * riusando la stessa config `ACTIVITIES` del builder per label/icone così la
 * libreria resta coerente con il linguaggio visivo già noto agli utenti.
 * Il filtro è una select a singola scelta (nessun tipo selezionato = mostra
 * tutti i tipi), coerente col filtro `ownerScope` già esistente in
 * `LibraryActivitiesTable`.
 *
 * La fetch (`useActivityLibraryQuery`) recupera le righe visibili all'utente filtrate
 * PER AMBITO lato server (`p_scope`): tipo attività, tag e ricerca testuale restano poi
 * filtri client-side, per evitare una chiamata di rete per ogni cambio filtro o carattere digitato.
 *
 * Ambiti:
 * - account `agency`: nessuna scelta, scope forzato ad 'agency' (personale ≡ agenzia);
 * - `travel_advisor_member`: sceglie personale/agenzia (`LibraryArchiveSelector`), le due
 *   viste derivano da due query per scope ('user' / 'agency');
 * - utente senza agenzia: scope fisso 'user';
 * - admin: nessuno scope (vede tutto), con il filtro ownerScope già esistente in
 *   `LibraryActivitiesTable`.
 */
const LibraryPage = () => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userRole, loading: authLoading } = useAuth();
  const { isAgencyAccount, isAgencyMember, canPromote } = useLibraryScope();

  const isAdmin = userRole === 'admin';

  // Il membro agenzia sceglie l'ambito (personale/agenzia) via query param `?scope=`;
  // finché non ha scelto mostriamo il selettore e non facciamo fetch.
  const scopeParam = searchParams.get('scope');
  const memberScope: LibraryArchiveScope | null = isAgencyMember
    ? scopeParam === 'agency' || scopeParam === 'user'
      ? scopeParam
      : null
    : null;
  const needsArchiveChoice = isAgencyMember && !memberScope;

  // Scope effettivo passato alla RPC:
  // - admin: null (tutte le righe visibili);
  // - account agency: 'agency' (personale ≡ agenzia);
  // - membro: lo scope scelto (null finché non sceglie → fetch disabilitata);
  // - utente semplice: 'user'.
  const queryScope: LibraryArchiveScope | null = isAdmin
    ? null
    : isAgencyAccount
      ? 'agency'
      : isAgencyMember
        ? memberScope
        : 'user';

  // La fetch parte SOLO quando l'auth è risolto (`!authLoading`): al primo render
  // `userRole`/`userData` sono null, quindi senza questo gate `queryScope` cadrebbe nel
  // ramo di default ('user') e partirebbe una chiamata spuria, poi rifatta con lo scope
  // reale una volta caricato l'utente (doppione visto per agenzia e per membro). Con l'auth
  // caricato: l'agenzia entra dritta nella libreria agenzia (fetch subito), il membro non
  // fetcha finché non sceglie l'archivio (`needsArchiveChoice`).
  const { data: activities = [], isLoading } = useActivityLibraryQuery(
    { scope: queryScope },
    { enabled: !authLoading && !needsArchiveChoice }
  );

  // Flusso "Aggiungi": scelto un tipo si apre il modal attività corrispondente del
  // builder (senza date, vedi `hideDates`); al salvataggio si incatena lo stesso
  // SaveToLibraryModal già usato dal builder per scegliere tag/ambito e persistere
  // la entry in libreria (stesso pattern di `handleSaveToLibrary` in DayBuilderStep).
  const [newActivityType, setNewActivityType] = useState<ActivityType | null>(null);
  const [activityToSaveToLibrary, setActivityToSaveToLibrary] = useState<ItineraryActivity | null>(null);
  // Ricerca testuale, proprietario e ambito: lo stato dei filtri vive qui, dove sta la
  // riga filtri; la tabella li riceve come prop e li applica al filtro client-side.
  const [search, setSearch] = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [ownerScope, setOwnerScope] = useState<LibraryOwnerScope>('all');

  const closeNewActivityModal = () => setNewActivityType(null);

  const handleNewActivitySave = (activity: Partial<ItineraryActivity>) => {
    setNewActivityType(null);
    setActivityToSaveToLibrary(activity as ItineraryActivity);
  };

  // Il filtro per ambito è ora fatto lato server (`p_scope`), quindi `activities`
  // è già ristretto allo scope corrente: nessun partizionamento client-side qui.
  // Il pulsante "Promuovi in libreria agenzia" ha senso solo per il membro che sta
  // guardando la propria libreria personale.
  const enablePromote = canPromote && memberScope === 'user';

  // "Rimuovi dalla libreria agenzia": ha senso nella vista agenzia, dove il proprietario
  // di una riga può riportarla a sola personale. La tabella la mostra comunque solo sulle
  // righe di cui l'utente corrente è il creatore (owner_id === me), coerente con la RPC.
  const enableDemote = queryScope === 'agency';

  // `accommodationOut` è una variante interna nascosta nel builder (stesso modal
  // di accommodation): non ha senso come filtro separato in libreria.
  const visibleActivityConfigs = useMemo(
    () => ACTIVITIES.filter((activityConfig) => !activityConfig.hidden),
    []
  );

  const typeParam = searchParams.get('type');
  const selectedActivityType: ActivityType | null = useMemo(() => {
    if (!typeParam) return null;
    const validIds = new Set(visibleActivityConfigs.map((activityConfig) => activityConfig.id));
    return validIds.has(typeParam as ActivityType) ? (typeParam as ActivityType) : null;
  }, [typeParam, visibleActivityConfigs]);

  const handleSelectActivityType = (value: ActivityType | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null) {
      next.delete('type');
    } else {
      next.set('type', value);
    }
    setSearchParams(next);
  };

  const typeOptions = useMemo(
    () =>
      visibleActivityConfigs.map((activityConfig) => ({
        value: activityConfig.id,
        // `labelKey` è tipizzato `string` in ActivityTypeConfig (usato dal builder generico);
        // stesso cast già adottato da DayBuilderStep.tsx/TimelineCard.tsx per lo stesso campo.
        label: t(activityConfig.labelKey as any), // eslint-disable-line @typescript-eslint/no-explicit-any
        icon: getActivityTypeIcon(activityConfig.id),
      })),
    [visibleActivityConfigs, t]
  );

  // Il filtro proprietario ha senso solo dove convivono righe di più persone: libreria
  // agenzia o vista admin. Le opzioni escono dalle attività già fetchate (nessuna query
  // in più) e restano quelle dell'intero dataset, non del sottoinsieme filtrato: una
  // lista che si accorcia mentre filtri toglie proprio le voci con cui cambiare filtro.
  const showOwnerColumn = isAdmin || queryScope === 'agency';
  const ownerOptions = useMemo(() => {
    if (!showOwnerColumn) return undefined;
    const byId = new Map<string, { value: string; label: string; hint?: string | null }>();
    activities.forEach((activity) => {
      if (!activity.owner_id || byId.has(activity.owner_id)) return;
      const fullName = [activity.owner_first_name, activity.owner_last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      const label = fullName || activity.owner_email;
      if (!label) return;
      byId.set(activity.owner_id, {
        value: activity.owner_id,
        label,
        // L'email distingue gli omonimi; ridondante se è già lei l'etichetta.
        hint: fullName ? activity.owner_email : null,
      });
    });
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [activities, showOwnerColumn]);

  const handleScopeSelect = (nextScope: LibraryArchiveScope) => {
    const next = new URLSearchParams(searchParams);
    next.set("scope", nextScope);
    setSearchParams(next);
  };

  const handleChangeArchive = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("scope");
    setSearchParams(next);
  };

  // Per il membro agenzia che ha già scelto un archivio, il titolo della pagina è
  // sostituito dal tasto "Indietro" (torna alla scelta archivio) + il nome
  // dell'archivio corrente ("Personale"/"Agenzia"). Negli altri casi resta il
  // titolo statico della libreria.
  const showArchiveTitle = isAgencyMember && !!memberScope;
  const pageTitle: ReactNode = showArchiveTitle ? (
    <span className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="icon"
        type="button"
        onClick={handleChangeArchive}
        aria-label={t('library.changeArchiveAction')}
        className="h-7 w-7 shrink-0 text-gray-500 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <span>
        {memberScope === 'agency'
          ? t('library.archiveChoiceAgencyAction')
          : t('library.archiveChoicePersonalAction')}
      </span>
    </span>
  ) : (
    t("library.title")
  );

  // Il tasto "Aggiungi" vive nell'header (slot `headerAction`, allineato a destra
  // come i CTA delle altre pagine), non più nella barra dei filtri. Presente solo
  // quando è mostrata la tabella (non durante il loading o la scelta dell'archivio).
  const showLibraryTable = !authLoading && !needsArchiveChoice;
  const headerAction: ReactNode = showLibraryTable ? (
    <ActivityTypePicker
      onSelect={setNewActivityType}
      label={t('library.addActivityAction')}
      variant="default"
      size="lg"
      className="h-8 px-3"
    />
  ) : undefined;

  // Stessa azione, ridotta a icona per la barra degli schermi stretti.
  const headerActionCompact: ReactNode = showLibraryTable ? (
    <ActivityTypePicker
      onSelect={setNewActivityType}
      label={t('library.addActivityAction')}
      variant="default"
      size="sm"
      className="h-8 whitespace-nowrap rounded-full px-2.5 text-xs [&>span]:inline"
    />
  ) : undefined;

  return (
    <PrivateLayout
      pageTitle={pageTitle}
      headerAction={headerAction}
      headerActionCompact={headerActionCompact}
      // Il sottotitolo passa sempre di qui, anche nel caso archivio: annidato dentro
      // `pageTitle` sfuggiva all'header, che sugli schermi stretti lo sposta nella (i).
      pageDescription={t("library.pageDescription")}
      contentClassName="h-full flex flex-col min-h-0 py-2 md:py-4"
    >
      <div className="flex flex-1 flex-col min-h-0 gap-4">
        {authLoading ? (
          // Finché non conosciamo il ruolo non sappiamo se mostrare il selettore d'archivio
          // (membro) o la libreria diretta (agenzia/utente): mostriamo un loader neutro per
          // non far lampeggiare la tabella prima di switchare al selettore.
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : needsArchiveChoice ? (
          <LibraryArchiveSelector onSelect={handleScopeSelect} />
        ) : (
          <>
            <div className="xl:px-4 md:px-4 w-full mx-auto flex flex-1 flex-col min-h-0 gap-4">
              <div className="shrink-0">
                <LibraryFilters
                  typeOptions={typeOptions}
                  selectedActivityType={selectedActivityType}
                  onActivityTypeChange={handleSelectActivityType}
                  ownerOptions={ownerOptions}
                  selectedOwnerId={selectedOwnerId}
                  onOwnerChange={setSelectedOwnerId}
                  showScopeFilter={isAdmin}
                  ownerScope={ownerScope}
                  onOwnerScopeChange={setOwnerScope}
                  search={search}
                  onSearchChange={setSearch}
                />
              </div>

              <div className="flex-1 min-h-0">
                <LibraryActivitiesTable
                  selectedActivityType={selectedActivityType}
                  search={search}
                  activities={activities}
                  isLoading={isLoading}
                  enablePromote={enablePromote}
                  enableDemote={enableDemote}
                  selectedOwnerId={selectedOwnerId}
                  ownerScope={ownerScope}
                  // In libreria personale (scope 'user') il proprietario è sempre l'utente
                  // corrente: colonna ridondante. La mostriamo solo in vista agenzia
                  // (righe di più membri) o admin (nessuno scope, vede tutto).
                  showOwner={showOwnerColumn}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {newActivityType &&
        (getActivityModal(newActivityType)({
          type: newActivityType,
          isOpen: true,
          onClose: closeNewActivityModal,
          onSave: handleNewActivitySave,
          icon: getActivityTypeIcon(newActivityType),
          itineraryId: LIBRARY_ACTIVITY_STORAGE_ID,
          hideDates: true,
        } as ModalProps) as ReactNode)}

      <SaveToLibraryModal
        isOpen={activityToSaveToLibrary !== null}
        onClose={() => setActivityToSaveToLibrary(null)}
        activity={activityToSaveToLibrary}
      />
    </PrivateLayout>
  );
};

export default LibraryPage;
